import { signal, computed, effect, batch } from "@preact/signals";
import {
  buildTimeline,
  cueAt,
  readScript,
  selectionRange,
  scriptCards,
  type PromptMode,
  type Script,
} from "./script";
import {
  cameraAdapter,
  demoAdapter,
  createRecorder,
  type CaptureSource,
  type Recording,
  type TakeSummary,
} from "./capture";
import { listTakes, saveTake } from "./storage";

export const script = signal<Script>(readScript(localStorage));
export const editorDraft = signal("");
export const editing = signal(false);
export const transferring = signal(false);
export const selection = signal<[number, number] | null>(null);
export const scope = signal("sentence");
export const mode = signal<"camera" | "demo">("camera");
export const facing = signal<"user" | "environment">("user");
export const source = signal<CaptureSource | null>(null);
export const status = signal<
  "idle" | "opening" | "starting" | "recording" | "rehearsing" | "saving"
>("idle");
export const elapsed = signal(0);
// Precise playback time for imperative animation, without rendering every frame.
export const playbackSeconds = signal(0);
export const message = signal("");
export const storageNotice = signal("");
export const takes = signal<TakeSummary[]>([]);
export const takesCount = signal(0);
export const hasMoreTakes = signal(false);
export const loadingTakes = signal(false);
export const currentTake = signal<Recording | null>(null);
export const timeline = computed(function () {
  return buildTimeline(script.value.words);
});
export const duration = computed(function () {
  return timeline.value.at(-1)?.end || 0;
});
export const activeIndex = signal(-1);
export const cards = computed(() => scriptCards(script.value.words));
export const cardIndex = signal(0);
export function advanceCard(): void {
  const running = status.value === "recording" || status.value === "rehearsing";
  if (script.value.mode === "cue-cards" && running) {
    cardIndex.value = Math.min(cardIndex.value + 1, cards.value.length - 1);
  }
}
export function changePromptMode(mode: PromptMode): void {
  if (busy.value) return;
  replaceScript({ ...script.value, mode });
}
export const busy = computed(function () {
  return status.value !== "idle";
});
let connectionId = 0;
let recorder: MediaRecorder | null = null;
let timer = 0;
let startedAt = 0;
let anchor: number | null = null;

// Persist the complete word annotations with the draft.
effect(function () {
  try {
    localStorage.setItem("cue-script", JSON.stringify(script.value));
  } catch {
    storageNotice.value =
      "Draft could not be saved on this device. Keep this tab open.";
  }
});

/** Replace text and annotations together, discarding selection from the old script. */
export function replaceScript(next: Script): void {
  anchor = null;
  selection.value = null;
  cardIndex.value = 0;
  script.value = next;
}

export function selectWord(index: number): void {
  if (script.value.mode === "cue-cards") {
    const card = cards.value.find(
      (card) => index >= card.start && index <= card.end,
    );
    if (card) selection.value = [card.start, card.end];
    return;
  }
  selection.value = selectionRange(
    script.value.words,
    index,
    scope.value,
    anchor,
  );
  if (scope.value === "range") anchor = anchor === null ? index : null;
}
export function changeScope(value: string): void {
  scope.value = value;
  anchor = null;
  let nextSelection: [number, number] | null = null;
  if (value === "all" && script.value.words.length) {
    nextSelection = [0, script.value.words.length - 1];
  }
  selection.value = nextSelection;
}
export function annotate(field: "pace" | "intensity", value: number): void {
  if (!selection.value) return;
  const [start, end] = selection.value;
  const words = script.value.words.map(function (word, index) {
    const isSelected = index >= start && index <= end;
    if (isSelected) return { ...word, [field]: value };
    return word;
  });
  script.value = { ...script.value, words };
}
export function setSelectionPause(pauseAfterMs: number): void {
  if (!selection.value) return;
  const validPause =
    Number.isInteger(pauseAfterMs) &&
    pauseAfterMs >= 0 &&
    pauseAfterMs <= 10000;
  if (!validPause) return;
  const end = selection.value[1];
  const words = [...script.value.words];
  words[end] = { ...words[end], pauseAfterMs };
  script.value = { ...script.value, words };
}
export async function loadTakes(): Promise<void> {
  if (loadingTakes.value) return;
  loadingTakes.value = true;
  try {
    const page = await listTakes(takes.value.at(-1));
    const knownIds = new Set(takes.value.map((take) => take.id));
    takes.value = [
      ...takes.value,
      ...page.takes.filter((take) => !knownIds.has(take.id)),
    ];
    takesCount.value = Math.max(page.total, takes.value.length);
    hasMoreTakes.value = page.hasMore;
  } catch {
    storageNotice.value =
      "Device storage is unavailable. Close other Cue tabs if an upgrade is waiting. Download each take before closing the app.";
  } finally {
    loadingTakes.value = false;
  }
}
export async function connect(): Promise<void> {
  if (busy.value) return;
  const requestId = ++connectionId;
  status.value = "opening";
  message.value = "";
  source.value?.dispose();
  source.value = null;
  try {
    const adapter = mode.value === "demo" ? demoAdapter : cameraAdapter;
    const opened = await adapter.open(facing.value);
    if (requestId !== connectionId) {
      opened.dispose();
      return;
    }
    const videoTrack = opened.stream.getVideoTracks()[0];
    const actualFacing = videoTrack?.getSettings().facingMode;
    if (actualFacing === "user" || actualFacing === "environment") {
      facing.value = actualFacing;
    }
    source.value = opened;
  } catch (error) {
    if (requestId !== connectionId) return;
    message.value =
      error instanceof Error
        ? `${error.message} You can also use Camera-free test.`
        : "Could not open the camera. Check camera and microphone permissions.";
  } finally {
    if (requestId === connectionId) status.value = "idle";
  }
}
export function disconnect(): void {
  connectionId++;
  if (status.value === "opening") status.value = "idle";
  source.value?.dispose();
  source.value = null;
}
function startClock(): void {
  cardIndex.value = 0;
  const manual = script.value.mode === "cue-cards";
  elapsed.value = 0;
  playbackSeconds.value = 0;
  activeIndex.value = cueAt(timeline.value, 0);
  startedAt = performance.now();
  function tick() {
    const seconds = (performance.now() - startedAt) / 1000;
    // Scroll uses precise time; labels and highlights change only at their boundaries.
    batch(function () {
      playbackSeconds.value = seconds;
      elapsed.value = Math.floor(seconds);
      if (!manual) activeIndex.value = cueAt(timeline.value, seconds);
    });
    if (!manual && seconds >= duration.value) {
      stop();
      return;
    }
    timer = requestAnimationFrame(tick);
  }
  timer = requestAnimationFrame(tick);
}
export function rehearse(): void {
  if (busy.value || !script.value.words.length) return;
  message.value = "";
  status.value = "rehearsing";
  startClock();
}
export function record(): void {
  if (busy.value || !source.value || !script.value.words.length) return;
  message.value = "";
  currentTake.value = null;
  status.value = "starting";
  const title = script.value.title;
  try {
    recorder = createRecorder(
      source.value.stream,
      function () {
        status.value = "recording";
        startClock();
      },
      async function (blob) {
        cancelAnimationFrame(timer);
        const recordedDuration = (performance.now() - startedAt) / 1000;
        status.value = "saving";
        const take: Recording = {
          id: crypto.randomUUID(),
          created: Date.now(),
          title,
          duration: recordedDuration,
          blob,
        };
        recorder = null;
        if (!blob.size) {
          message.value = "The recording was empty. Please try again.";
          status.value = "idle";
          return;
        }
        currentTake.value = take;
        const { blob: recordedBlob, ...summary } = take;
        takes.value = [summary, ...takes.value];
        takesCount.value++;
        try {
          await saveTake(take);
        } catch {
          storageNotice.value =
            "This take could not be saved on this device. Download it before closing the tab.";
        }
        status.value = "idle";
      },
      function (error) {
        message.value = error;
        stop();
      },
    );
    recorder.start(1000);
  } catch (error) {
    status.value = "idle";
    message.value =
      error instanceof Error ? error.message : "Recording could not start.";
  }
}
export function stop(): void {
  cancelAnimationFrame(timer);
  if (recorder && recorder.state !== "inactive") {
    status.value = "saving";
    recorder.stop();
  } else if (status.value === "rehearsing") {
    status.value = "idle";
  }
}
window.addEventListener("pagehide", function () {
  stop();
  disconnect();
});
window.addEventListener("beforeunload", function (event) {
  if (busy.value || currentTake.value) {
    event.preventDefault();
  }
});
void loadTakes();
